/**
 * Prevents the iOS virtual keyboard from appearing when the +/- spin buttons
 * of a PrimeNG p-inputNumber are pressed, while preserving the long-press repeat behavior.
 *
 * On iOS, the browser synthesizes mousedown *after* touchend, which is too late for
 * PrimeNG's repeat timer. We prevent that synthesis, fire mousedown immediately, and
 * use readonly to suppress the keyboard during the press without breaking PrimeNG's
 * internal focus() call or its direct element.value writes.
 */
export function preventKeyboardOnInputNumberButtonPress(event: TouchEvent): void {
    const target = event.target as HTMLElement;
    const button = target.closest('button') as HTMLElement;
    if (!button) { return; }
    const input = (button.closest('.p-inputnumber') as HTMLElement)?.querySelector('input') as HTMLInputElement;
    if (!input) { return; }

    // iOS won't show the keyboard for readonly inputs, even on programmatic focus().
    // Set readonly before dispatching mousedown so PrimeNG's internal focus() call is harmless.
    // PrimeNG writes values via element.value = ... directly, so spin still works while readonly.
    input.setAttribute('readonly', 'true');

    // Prevent iOS from synthesizing mousedown *after* touchend (too late for PrimeNG's repeat timer)
    // and manually fire it now so the repeat starts immediately
    event.preventDefault();
    button.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));

    const stopSpin = () => {
        document.removeEventListener('touchend', stopSpin);
        document.removeEventListener('touchcancel', stopSpin);
        button.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
        setTimeout(() => { input.removeAttribute('readonly'); input.blur(); }, 100);
    };

    document.addEventListener('touchend', stopSpin, { once: true });
    document.addEventListener('touchcancel', stopSpin, { once: true });
}
